import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import remarkBreaks from 'remark-breaks';
import { parseArtifacts } from '../utils/artifactParser';
import ArtifactCard from './ArtifactCard';
import { preprocessMath } from '../utils/mathUtils';

const MessageWithInlineArtifacts = ({ 
  messageText, 
  onArtifactOpen, 
  markdownComponents, 
  className = "markdown-content leading-relaxed text-base text-black dark:text-gray-200" 
}) => {
  const { artifacts, cleanText } = parseArtifacts(messageText);

  if (artifacts.length === 0) {
    // No artifacts, render normal markdown
    return (
      <ReactMarkdown
        className={className}
        remarkPlugins={[remarkGfm, remarkMath, remarkBreaks]}
        rehypePlugins={[rehypeRaw, rehypeKatex]}
        components={markdownComponents}
      >
        {preprocessMath(messageText)}
      </ReactMarkdown>
    );
  }

  // Split text by artifact placeholders and render inline
  const renderInlineContent = () => {
    const parts = [];
    let currentIndex = 0;

    // Find all placeholders and split the text
    const placeholderRegex = /\[ARTIFACT_PLACEHOLDER_(\d+)\]/g;
    let match;
    let lastIndex = 0;

    while ((match = placeholderRegex.exec(cleanText)) !== null) {
      // Add text before the placeholder
      if (match.index > lastIndex) {
        const textBefore = cleanText.slice(lastIndex, match.index);
        if (textBefore.trim()) {
          parts.push({
            type: 'text',
            content: textBefore,
            key: `text-${currentIndex++}`
          });
        }
      }

      // Add the artifact
      const artifactIndex = parseInt(match[1], 10);
      if (artifacts[artifactIndex]) {
        parts.push({
          type: 'artifact',
          content: artifacts[artifactIndex],
          key: `artifact-${currentIndex++}`
        });
      }

      lastIndex = placeholderRegex.lastIndex;
    }

    // Add remaining text after the last placeholder
    if (lastIndex < cleanText.length) {
      const remainingText = cleanText.slice(lastIndex);
      if (remainingText.trim()) {
        parts.push({
          type: 'text',
          content: remainingText,
          key: `text-${currentIndex++}`
        });
      }
    }

    return parts;
  };

  const contentParts = renderInlineContent();

  return (
    <>
      {contentParts.map((part) => {
        if (part.type === 'text') {
          return (
            <ReactMarkdown
              key={part.key}
              className={className}
              remarkPlugins={[remarkGfm, remarkMath, remarkBreaks]}
              rehypePlugins={[rehypeRaw, rehypeKatex]}
              components={markdownComponents}
            >
              {preprocessMath(part.content)}
            </ReactMarkdown>
          );
        } else if (part.type === 'artifact' && onArtifactOpen) {
          return (
            <ArtifactCard
              key={part.key}
              artifact={part.content}
              onClick={() => onArtifactOpen(part.content)}
            />
          );
        }
        return null;
      })}
    </>
  );
};

export default MessageWithInlineArtifacts;